/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   workspacesService.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   Frontend API service for Workspace Inventory dashboard
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import { apiClient } from '../apiClient';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { WorkspaceListResponse, WorkspaceInventory, CapacityType } from '../../types/operations';

// ============================================================================
// Query Key Factory
// ============================================================================

export const OPERATIONS_WORKSPACE_KEYS = {
  all: ['operations', 'workspaces'] as const,
  inventory: () => [...OPERATIONS_WORKSPACE_KEYS.all, 'inventory'] as const,
  lists: () => [...OPERATIONS_WORKSPACE_KEYS.all, 'list'] as const,
  list: (filters: WorkspaceListFilters) => [...OPERATIONS_WORKSPACE_KEYS.lists(), filters] as const,
  details: () => [...OPERATIONS_WORKSPACE_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...OPERATIONS_WORKSPACE_KEYS.details(), id] as const,
};

export interface WorkspaceListFilters {
  capacityType?: CapacityType;
  workspaceType?: string; // FR-009
  clientAccountId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface WorkspaceInventorySummary {
  totalWorkspaces: number;
  byCapacityType: Record<string, number>;
  totalItems: number;
  byItemType: {
    lakehouse: number; warehouse: number; notebook: number;
    pipeline: number; report: number; semanticModel: number;
  };
  personalCount: number;   // FR-009a
  unassignedCount: number; // FR-009a
  lastUpdated: string;
}

// ============================================================================
// API Functions
// ============================================================================

export async function getWorkspaceInventory(): Promise<WorkspaceInventorySummary> {
  const response = await apiClient.get<{ data: WorkspaceInventorySummary }>('/api/operations/workspaces/inventory');
  return response.data.data;
}

export async function getWorkspaceList(filters: WorkspaceListFilters = {}): Promise<WorkspaceListResponse> {
  const params = new URLSearchParams();
  if (filters.capacityType) params.append('capacityType', filters.capacityType);
  if (filters.workspaceType) params.append('workspaceType', filters.workspaceType);
  if (filters.clientAccountId) params.append('clientAccountId', filters.clientAccountId);
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

  const queryString = params.toString();
  const url = queryString ? `/api/operations/workspaces?${queryString}` : '/api/operations/workspaces';
  const response = await apiClient.get<{ data: WorkspaceListResponse }>(url);
  return response.data.data;
}

export async function getWorkspaceById(id: string): Promise<WorkspaceInventory> {
  const response = await apiClient.get<{ data: WorkspaceInventory }>(`/api/operations/workspaces/${id}`);
  return response.data.data;
}

// ============================================================================
// React Query Hooks
// ============================================================================

export function useWorkspaceInventory(options?: Omit<UseQueryOptions<WorkspaceInventorySummary>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: OPERATIONS_WORKSPACE_KEYS.inventory(),
    queryFn: getWorkspaceInventory,
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...options,
  });
}

export function useWorkspaceList(filters: WorkspaceListFilters = {}, options?: Omit<UseQueryOptions<WorkspaceListResponse>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: OPERATIONS_WORKSPACE_KEYS.list(filters),
    queryFn: () => getWorkspaceList(filters),
    staleTime: 30_000,
    ...options,
  });
}

export function useWorkspaceDetail(id: string | null, options?: Omit<UseQueryOptions<WorkspaceInventory>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: OPERATIONS_WORKSPACE_KEYS.detail(id || ''),
    queryFn: () => getWorkspaceById(id!),
    enabled: !!id,
    staleTime: 30_000,
    ...options,
  });
}
